import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, Box, Typography, IconButton, Chip,
  CircularProgress, Alert, Slide, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  CalendarMonth as CalendarIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useEmpresa } from '../../context/EmpresaContext';
import config from '../../config';

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const DIAS_SEMANA = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do'];

const CalendarioAsistencia = ({ open, onClose, trabajador, onUpdate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const { simboloMoneda } = useEmpresa();
  const moneda = simboloMoneda || 'S/';

  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && trabajador) fetchAsistencia();
  }, [open, trabajador, mes, anio]);

  const fetchAsistencia = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/api/liquidacion/asistencia/${trabajador.id_trabajador}?mes=${mes}&anio=${anio}`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setData(res.data);
    } catch (e) {
      setError('Error al cargar asistencia');
    } finally {
      setLoading(false);
    }
  };

  const toggleDia = async (fecha) => {
    const fechaStr = fecha.toISOString().split('T')[0];
    const yaRegistrado = data?.asistencias?.some(a =>
      new Date(a.fecha).toISOString().split('T')[0] === fechaStr && a.origen === 'MANUAL'
    );

    setSaving(true);
    try {
      if (yaRegistrado) {
        await axios.delete(
          `${config.API_BASE_URL}/api/liquidacion/asistencia/${trabajador.id_trabajador}/${fechaStr}`,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
      } else {
        await axios.post(
          `${config.API_BASE_URL}/api/liquidacion/asistencia`,
          { id_trabajador: trabajador.id_trabajador, fecha: fechaStr },
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
      }
      await fetchAsistencia();
      if (onUpdate) onUpdate();
    } catch (e) {
      setError(e.response?.data?.error || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const cambiarMes = (dir) => {
    let nuevoMes = mes + dir;
    let nuevoAnio = anio;
    if (nuevoMes > 12) { nuevoMes = 1; nuevoAnio++; }
    if (nuevoMes < 1) { nuevoMes = 12; nuevoAnio--; }
    setMes(nuevoMes);
    setAnio(nuevoAnio);
  };

  // Generar días del calendario
  const diasMes = data?.dias_mes || new Date(anio, mes, 0).getDate();
  const primerDia = new Date(anio, mes - 1, 1).getDay(); // 0=Dom
  const offset = primerDia === 0 ? 6 : primerDia - 1; // Ajustar para Lunes=0

  const asistenciaMap = {};
  (data?.asistencias || []).forEach(a => {
    const key = new Date(a.fecha).toISOString().split('T')[0];
    if (!asistenciaMap[key]) asistenciaMap[key] = [];
    asistenciaMap[key].push(a);
  });

  const esHoy = (dia) => {
    const hoy = new Date();
    return dia === hoy.getDate() && mes === hoy.getMonth() + 1 && anio === hoy.getFullYear();
  };

  const esFuturo = (dia) => {
    const fecha = new Date(anio, mes - 1, dia);
    return fecha > new Date();
  };

  const tipoPago = trabajador?.tipo_pago || data?.tipo_pago || 'COMISION';
  const colorTipo = tipoPago === 'SUELDO' ? '#2E7D32' : '#E65100';
  const nombreMes = new Date(anio, mes - 1).toLocaleString('es', { month: 'long' });

  return (
    <Dialog
      open={open} onClose={onClose}
      fullScreen={isMobile} maxWidth="xs" fullWidth
      TransitionComponent={isMobile ? Transition : undefined}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3, overflow: 'hidden' } }}
    >
      {/* Header */}
      <Box sx={{
        background: `linear-gradient(135deg, ${colorTipo}, ${colorTipo}CC)`,
        color: '#fff', px: 2.5, py: 2,
        display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CalendarIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 800 }}>
            Asistencia — {trabajador?.nombre_trabajador} {trabajador?.apellido_trabajador}
          </Typography>
          <Typography sx={{ fontSize: 12, opacity: 0.8 }}>
            {tipoPago === 'SUELDO' ? `Sueldo: ${moneda} ${parseFloat(trabajador?.monto_sueldo || 0).toFixed(2)}/mes` :
             `Jornal: ${moneda} ${parseFloat(trabajador?.monto_jornal || 0).toFixed(2)}/día`}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {error && <Alert severity="error" sx={{ m: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* Nav mes */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <IconButton onClick={() => cambiarMes(-1)} size="small"><PrevIcon /></IconButton>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1E293B', textTransform: 'capitalize' }}>
            {nombreMes} {anio}
          </Typography>
          <IconButton onClick={() => cambiarMes(1)} size="small"><NextIcon /></IconButton>
        </Box>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : (
          <>
            {/* Headers días */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', px: 2, mb: 0.5 }}>
              {DIAS_SEMANA.map(d => (
                <Typography key={d} sx={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#94A3B8' }}>
                  {d}
                </Typography>
              ))}
            </Box>

            {/* Días */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, px: 2, pb: 2 }}>
              {/* Espacios vacíos antes del día 1 */}
              {Array.from({ length: offset }).map((_, i) => <Box key={`e-${i}`} />)}

              {Array.from({ length: diasMes }).map((_, i) => {
                const dia = i + 1;
                const fecha = new Date(anio, mes - 1, dia);
                const fechaStr = fecha.toISOString().split('T')[0];
                const registros = asistenciaMap[fechaStr] || [];
                const tieneAsistencia = registros.length > 0;
                const esManual = registros.some(r => r.origen === 'MANUAL');
                const esServicio = registros.some(r => r.origen === 'SERVICIO');
                const esApp = registros.some(r => r.origen === 'APP');
                const liquidado = registros.some(r => r.liquidado);
                const futuro = esFuturo(dia);
                const hoy = esHoy(dia);

                return (
                  <Box
                    key={dia}
                    onClick={() => !futuro && !liquidado && toggleDia(fecha)}
                    sx={{
                      position: 'relative',
                      aspectRatio: '1',
                      borderRadius: 2,
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      cursor: futuro || liquidado ? 'default' : 'pointer',
                      bgcolor: tieneAsistencia
                        ? (liquidado ? '#E8F5E9' : `${colorTipo}15`)
                        : (hoy ? '#F0F4FF' : 'transparent'),
                      border: '1.5px solid',
                      borderColor: hoy ? '#1A237E' : (tieneAsistencia ? colorTipo : '#E2E8F0'),
                      opacity: futuro ? 0.3 : 1,
                      transition: 'all 0.15s',
                      '&:hover': !futuro && !liquidado ? { transform: 'scale(1.08)', borderColor: colorTipo } : {},
                    }}
                  >
                    <Typography sx={{
                      fontSize: 13, fontWeight: hoy ? 800 : 600,
                      color: tieneAsistencia ? colorTipo : '#1E293B',
                    }}>
                      {dia}
                    </Typography>
                    {/* Indicadores */}
                    {tieneAsistencia && (
                      <Box sx={{ display: 'flex', gap: 0.3, mt: 0.2 }}>
                        {esServicio && <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#3B82F6' }} />}
                        {esManual && <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: colorTipo }} />}
                        {esApp && <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#8B5CF6' }} />}
                      </Box>
                    )}
                    {liquidado && (
                      <CheckIcon sx={{ position: 'absolute', top: 1, right: 1, fontSize: 10, color: '#2E7D32' }} />
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* Leyenda */}
            <Box sx={{ display: 'flex', gap: 2, px: 2, pb: 1.5, flexWrap: 'wrap' }}>
              <LeyendaDot color={colorTipo} label="Manual (admin)" />
              <LeyendaDot color="#3B82F6" label="Por servicio" />
              <LeyendaDot color="#8B5CF6" label="Desde la app" />
            </Box>

            {/* Resumen */}
            {data && (
              <Box sx={{
                mx: 2, mb: 2, p: 2, borderRadius: 3,
                bgcolor: `${colorTipo}08`, border: `1px solid ${colorTipo}25`,
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: colorTipo }}>{data.dias_trabajados}</Typography>
                    <Typography sx={{ fontSize: 10, color: '#64748B' }}>Días trabajados</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#1E293B' }}>{data.dias_mes}</Typography>
                    <Typography sx={{ fontSize: 10, color: '#64748B' }}>Días del mes</Typography>
                  </Box>
                  {tipoPago === 'SUELDO' && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#EF4444' }}>{data.dias_falta}</Typography>
                      <Typography sx={{ fontSize: 10, color: '#64748B' }}>Faltas</Typography>
                    </Box>
                  )}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#10B981' }}>
                      {moneda} {data.total_monto?.toFixed(2)}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: '#64748B' }}>
                      {tipoPago === 'SUELDO' ? 'Acumulado' : 'Total jornales'}
                    </Typography>
                  </Box>
                </Box>
                {tipoPago === 'SUELDO' && data.descuento_inasistencia > 0 && (
                  <Typography sx={{ fontSize: 12, color: '#EF4444', textAlign: 'center', mt: 0.5 }}>
                    Descuento por inasistencia: {moneda} {data.descuento_inasistencia.toFixed(2)}
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      {/* Footer */}
      <Box sx={{ borderTop: '1px solid #E2E8F0', p: 2 }}>
        <Box onClick={onClose} sx={{
          py: 1.2, borderRadius: 2.5, textAlign: 'center',
          border: '1.5px solid #E2E8F0', cursor: 'pointer',
          fontSize: 14, fontWeight: 600, color: '#64748B',
          '&:hover': { bgcolor: '#F8FAFC' },
        }}>
          Cerrar
        </Box>
      </Box>
    </Dialog>
  );
};

const LeyendaDot = ({ color, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
    <Typography sx={{ fontSize: 11, color: '#64748B' }}>{label}</Typography>
  </Box>
);

export default CalendarioAsistencia;
